// ============================================================
//  ★ 데이터 SEAM — 개발자 작업은 "여기"만 ★
// ------------------------------------------------------------
//  변환 산출물의 유일한 데이터 접점. fn 내부를 2.0 API 호출로 교체했다.
//  (핸드오프 규칙 — 컬럼/필터/테이블 UI 는 훅 계약(contents/totalCount) 그대로 소비)
//  BE: GET /api/v1/master/products (LOGISPRD-9612, master_item/master_item_option)
//  - 필터 시맨틱: shipperCode/styleNumber/uid/optionCode = eq, serviceTypes = 화주군, 나머지 = contains
//  - 다건 입력 필드는 단건이면 서버 필터, 다건이면 값별 서버 필터 요청 확장 후 병합·중복 제거 (searchQuery.ts)
// ============================================================
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'
import { formatDateTime } from '@/pages/master/userManagement/constants'
import { buildServerQueries, dedupeByItemId, matchesClientFilters } from './searchQuery'

export interface ProductManagementRow {
  /** 수정(PUT /{itemId}) 대상 식별자 — 2.0 응답 itemId. 화면 표시엔 미사용 */
  _id: number
  /** PUT 전체 교체 대비 원본 보존 필드 (화면 비편집) */
  _companyName: string | null
  _unitCode: string | null
  service_type: string
  shipper_name: string
  SPR_NM: string
  GDS_GROUP1: string
  BRD_NM: string
  STL_NO: string
  BARCODE: string
  GDS_CD: string
  GDS_NM: string
  GDS_OPT: string
  GDS_COLOR: string
  GDS_ATTR1: string
  GDS_TYPE: string
  PLT_CD: string
  GDS_LOCATION: string
  WH_NM: string
  WH_AREA: string
  AP_PRICE: number | null
  AR_PRICE: number | null
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
  UID: string
  option_code: string
  outbound_priority: number | null
  first_inbound_date: string
  product_image_url: string
}

export interface ProductManagementParams {
  /** 조회 시퀀스 — 동일 조건 재조회용 queryKey 구분자 (서버 미전달) */
  _seq?: number
  /** 화주유형 (BE serviceTypes — 화주군 필터) */
  serviceTypes?: string[]
  /** 화주코드 (BE shipperCode eq) — 다건은 값별 서버 요청 확장 또는 클라 IN (searchQuery.ts) */
  shipperCodes?: string[]
  /** 브랜드명 (BE brandName contains) — 다건은 값별 서버 요청 확장 또는 클라 contains-OR */
  brandNames?: string[]
  /** 스타일번호 (BE styleNumber eq) — 다건은 값별 서버 요청 확장 또는 클라 IN */
  styleNumbers?: string[]
  /** 상품코드 (BE itemCode contains) — 다건은 값별 서버 요청 확장 또는 클라 contains-OR */
  itemCodes?: string[]
  /** 바코드 (BE barcode contains) — 다건은 값별 서버 요청 확장 또는 클라 contains-OR */
  barcodes?: string[]
  /** 옵션코드 (BE optionCode Long eq) — 다건은 값별 서버 요청 확장 또는 클라 IN */
  optionCodes?: string[]
}

export interface ProductManagementResponse {
  /** 화주타입/화주명은 비운다 — 화면이 렌더마다 화주 목록과 조인한다(캐시에 조회 시점 화주 목록이 굳지 않게, 14201) */
  contents: Omit<ProductManagementRow, 'service_type' | 'shipper_name'>[]
  totalCount: number
  /** 페이지 순회 안전 상한(MAX_PAGES) 초과로 일부만 로드됨 — 화면에서 명시 경고 대상 */
  truncated: boolean
}

interface MasterProductItem {
  itemId: number
  shipperCode: string
  barcode: string
  skuId?: string | null
  itemCode?: string | null
  itemOption?: string | null
  itemName?: string | null
  category?: string | null
  color?: string | null
  styleNumber?: string | null
  brandId?: string | null
  brandName?: string | null
  companyName?: string | null
  itemAttribute?: string | null
  uid?: string | null
  unitCode?: string | null
  receivablePrice?: number | null
  payablePrice?: number | null
  productImageUrl?: string | null
  optionId?: number | null
  optionCode?: number | null
  outboundPriority?: number | null
  firstInboundDate?: string | null
  // BE LocalDateTime 배열 직렬화([y,M,d,H,m,s]) — formatDateTime 이 배열/문자열 겸용 처리
  createdAt?: string | number[] | null
  lastModifiedAt?: string | number[] | null
}

/** 화주코드 → {shipperName, serviceType} 조인용 (화주타입/화주명 표시 컬럼은 응답에 없어 FE 파생) */
export interface ShipperInfoMap {
  [shipperCode: string]: { shipperName: string; serviceType: string }
}

const fmtDate = (v?: string | number[] | null) => (v == null ? '' : formatDateTime(v).slice(0, 10))

// [MOMS 규칙] as-is 페이지 크기 10,000 고정 — 1.0 은 페이징 없이 전체 로드
const PAGE_SIZE = 10000
// 페이지 순회 안전 상한 (30 × 10,000 = 30만 행) — MA02 최대 화주(MUSINSA 19.9만 행) 여유 포함.
// 상한 초과분은 truncated 로 반환해 화면에서 명시 경고한다 (무경고 누락 금지)
const MAX_PAGES = 30

/** totalCount 만큼 페이지를 순회해 전체 로드 — 단일 page 0 요청의 페이지 경계 누락 방지 */
const fetchAllPages = async (
  query: Record<string, unknown>,
): Promise<{ items: MasterProductItem[]; totalCount: number; truncated: boolean }> => {
  const items: MasterProductItem[] = []
  let totalCount = 0
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await request.get<
      ApiResponseV2<{ items: MasterProductItem[]; pageInfo: { totalCount: number } }>,
      Record<string, unknown>
    >('/api/v1/master/products', { ...query, page, size: PAGE_SIZE })
    const pageItems = res.data?.items ?? []
    items.push(...pageItems)
    totalCount = res.data?.pageInfo?.totalCount ?? items.length
    // 전량 수신 또는 마지막 페이지(부분 페이지) — 서버가 page 를 무시해도 루프가 끝나도록 이중 종료 조건
    if (items.length >= totalCount || pageItems.length < PAGE_SIZE) return { items, totalCount, truncated: false }
  }
  return { items, totalCount, truncated: true }
}

const fn = async (params: ProductManagementParams): Promise<ProductManagementResponse> => {
  // 다건 필드는 값별 서버 필터 요청으로 확장(shipperCode 등 단건 파라미터는 항상 유지) — 순차 호출로 서버 부하 완화
  const pages: { items: MasterProductItem[]; totalCount: number; truncated: boolean }[] = []
  for (const query of buildServerQueries(params)) {
    pages.push(await fetchAllPages(query))
  }
  const contents = dedupeByItemId(pages.flatMap((p) => p.items))
    .filter((i) => matchesClientFilters(i, params))
    .map((i) => ({
      _id: i.itemId,
      _companyName: i.companyName ?? null,
      _unitCode: i.unitCode ?? null,
      SPR_NM: i.shipperCode,
      GDS_GROUP1: i.category ?? '',
      BRD_NM: i.brandName ?? '',
      STL_NO: i.styleNumber ?? '',
      BARCODE: i.barcode,
      GDS_CD: i.itemCode ?? '',
      GDS_NM: i.itemName ?? '',
      GDS_OPT: i.itemOption ?? '',
      GDS_COLOR: i.color ?? '',
      GDS_ATTR1: i.itemAttribute ?? '',
      // TODO(api): 상품유형/플랜트코드/상품위치/센터명/구역명 — master_item 에 원천 컬럼 없음 (LLD-9612 §스킵 결정)
      GDS_TYPE: '',
      PLT_CD: '',
      GDS_LOCATION: '',
      WH_NM: '',
      WH_AREA: '',
      AP_PRICE: i.payablePrice ?? null,
      AR_PRICE: i.receivablePrice ?? null,
      INS_DATE: fmtDate(i.createdAt),
      // TODO(api): 생성자/수정자 — 2.0 조회 응답에 감사 주체 필드 없음
      INS_BY: '',
      UPD_DATE: fmtDate(i.lastModifiedAt),
      UPD_BY: '',
      UID: i.uid ?? '',
      option_code: i.optionCode != null ? String(i.optionCode) : '',
      outbound_priority: i.outboundPriority ?? null,
      first_inbound_date: i.firstInboundDate ?? '',
      product_image_url: i.productImageUrl ?? '',
    }))
  // 병합·중복 제거 후에는 서버 totalCount 합이 의미 없다 — 실제 로드 건수를 반환 (truncated 시 화면 경고)
  return { contents, totalCount: contents.length, truncated: pages.some((p) => p.truncated) }
}

export const useGetProductManagementList = (
  params: ProductManagementParams,
  enabled = true,
): UseQueryResult<ProductManagementResponse, Error> =>
  useQuery({
    queryKey: ['getProductManagementList', params],
    queryFn: () => fn(params),
    enabled,
    meta: { noErrorMessage: true }, // 실패 안내는 화면이 새 디자인 알럿으로 한 번 낸다(usePboQueryErrorToast → notice.failure — 옛 전역 알럿 대신)
  })
